import * as React from "react";
import {cx} from "emotion";

import "./radius-side-bar.scss";

import {cs}                from "@Common/react/chain-services";
import {UseState}          from "@Common/react/use-state";
import {consumeContext}    from "@Common/react/context";
import {Watch}             from "@Common/react/watch";
import {equalDeep, values} from "@Common/utils/objects";
import {cascadeFind}       from "@Common/utils/cascade";

import {AddressSearchSection}    from "./address-search-section/address-search-section";
import {AddressSelectionSection} from "./address-selection-section/address-selection-section";
import {GradualLoader}           from "../../step-1-search/streets-section/gradual-loader";
import {getStreet}               from "../../step-1-search/streets-section/common/get-street";

export const RadiusSideBar = ({streets, addresses, loading, view, viewSize, searchedForm, onSearch, searchError, renderErrorNoti, next}) => cs(
    consumeContext("apis"),
    ["show", (_, next) => UseState({
        initValue: true,
        next,
    })],
    ["addressSearchSection", ({}, next) => AddressSearchSection({onSearch, next})],
    ["addressSelectionSection", ({}, next) => AddressSelectionSection({streets, addresses, view, next})],
    ["addressesLoader", ({apis}, next) => {
        const mapStreetRecordToRequest = (street) => ({
            streetAddress: street.address,
            streetZip: street.zip_code,
        });

        return GradualLoader({
            getKey: (street) => JSON.stringify([
                searchedForm,
                mapStreetRecordToRequest(street),
            ]),
            fetch: async ({feeds: streets, searchForm=undefined}) => {
                const result = await apis.next.mailing.searchRadiusStreetAddresses({
                    searchForm: searchForm ?? searchedForm,
                    streetAddresses: streets.map(mapStreetRecordToRequest),
                });

                if (result.result === "failure") {
                    return false;
                }

                return streets.map((s) => result[s.address]);
            },

            next,
        });
    }],
    ({addressesLoader}, next) => Watch({
        value: Object.keys(streets.value?.streets ?? {}),
        compare: equalDeep,
        onChanged: async (newStreets) => {
            if(newStreets.length) {
                addresses.onChange(null);
                const loadStreets = values(streets.value.streets).map((street) => getStreet(street));
                const addresses1 = await addressesLoader.loadAll({feeds: loadStreets, loading});

                if (addresses1.result === "failed") {
                    renderErrorNoti({
                        msg: "An error occurred while retrieving the addresses. " +
                            "Please try again or contact support if the issue persists.",
                        color: "red",
                        duration: 10000
                    })
                    view.onChange("search");
                } else {
                    if (cascadeFind(addresses1, "[*].result[*].pos.isMatched", (isMatched) => !isMatched)) {
                        renderErrorNoti({
                            msg: "Some addresses could not be plotted. " +
                                "These addresses will still show in the table and be included in the mailing list.",
                            color: "gray"
                        });
                    }
                    addresses.onChange(addresses1.reduce((acc, {feed, result}) => ({...acc, [feed.address]: result}), {}));
                } 
            }
        },
        next,
    }),

    ({show, addressSearchSection, addressSelectionSection}) => next({
        getNextAction: ({onNext, loadErrorHighlight}) => addressSelectionSection.getNextAction({onNext, loading, loadErrorHighlight}),
        createRequestData: addressSelectionSection.createRequestData,
        render: () => (
            <div className={cx("radius-side-bar-e5r", {disabled: !show.value})}>
                <div {...{
                    className: "toggle",
                    "xd-qa-name": "Build Radius List Toggle",
                    "xd-qa-type": "button",
                    onClick: () => show.onChange(!show.value)
                }}>
                    <span>{show.value ? "<" : ">"}</span>
                </div>
                <div className="sidebar">
                    <div className={cx("content", viewSize)}>
                        {view.value === "search" ? (
                            addressSearchSection.render({searchError})
                        ) : (
                            addressSelectionSection.render({target: addressSearchSection.target})
                        )}
                    </div>
                </div>
            </div>
        ),
    })
);